import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock3, Link2, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import CATTest from '../features/assessments-v2/tests/CATTest';
import ACTTest from '../features/assessments-v2/tests/ACTTest';
import MMRCTest from '../features/assessments-v2/tests/MMRCTest';
import HADSTest from '../features/assessments-v2/tests/HADSTest';
import PSQITest from '../features/assessments-v2/tests/PSQITest';
import AQLQTest from '../features/assessments-v2/tests/AQLQTest';
import SGRQTest from '../features/assessments-v2/tests/SGRQTest';
import LCADLTest from '../features/assessments-v2/tests/LCADLTest';
import MLHFQTest from '../features/assessments-v2/tests/MLHFQTest';
import PFSDQMTest from '../features/assessments-v2/tests/PFSDQMTest';
import SAQTest from '../features/assessments-v2/tests/SAQTest';
import {
  ASSESSMENT_V2_QUESTIONNAIRE_TESTS,
  getAssessmentV2Label,
  isAssessmentV2QuestionnaireTest,
  type AssessmentV2TestKey,
} from '../features/assessments-v2/registry/assessmentRegistry';
import { AssessmentInvitesV2Service } from '../features/assessments-v2/services/AssessmentInvitesV2Service';
import { buildQuestionnaireAssessmentResultInput, buildQuestionnaireSharedContext } from '../features/assessments-v2/services/AssessmentQuestionnaireComposer';
import type {
  AssessmentInviteV2Preview,
  AssessmentInviteV2StartPayload,
  AssessmentResultV2,
} from '../features/assessments-v2/types';

const mapResultsByTest = (results: AssessmentResultV2[]): Partial<Record<AssessmentV2TestKey, AssessmentResultV2>> =>
  results.reduce((accumulator, result) => {
    if (isAssessmentV2QuestionnaireTest(result.testKey)) {
      accumulator[result.testKey] = result;
    }
    return accumulator;
  }, {} as Partial<Record<AssessmentV2TestKey, AssessmentResultV2>>);

const normalizeQuestionnaireTests = (tests: string[]): AssessmentV2TestKey[] =>
  tests.filter((testKey): testKey is AssessmentV2TestKey => ASSESSMENT_V2_QUESTIONNAIRE_TESTS.includes(testKey as any));

const getNextPendingQuestionnaire = (
  selectedTests: AssessmentV2TestKey[],
  resultsByTest: Partial<Record<AssessmentV2TestKey, AssessmentResultV2>>,
  currentTest?: AssessmentV2TestKey | null,
): AssessmentV2TestKey | null => {
  if (!selectedTests.length) return null;

  const currentIndex = currentTest ? selectedTests.indexOf(currentTest) : -1;
  for (let index = currentIndex + 1; index < selectedTests.length; index += 1) {
    const candidate = selectedTests[index];
    if (!resultsByTest[candidate] || resultsByTest[candidate]?.status === 'cancelled') {
      return candidate;
    }
  }

  return selectedTests.find((testKey) => !resultsByTest[testKey] || resultsByTest[testKey]?.status === 'cancelled') ?? null;
};

const getInitialQuestionnaireValue = (
  testKey: AssessmentV2TestKey,
  resultsByTest: Partial<Record<AssessmentV2TestKey, AssessmentResultV2>>,
) => {
  const storedResult = resultsByTest[testKey];
  const payload =
    storedResult?.payloadJson && typeof storedResult.payloadJson === 'object' ? { ...storedResult.payloadJson } : undefined;

  if (!payload) return undefined;

  switch (testKey) {
    case 'AQLQ':
    case 'PSQI':
    case 'SAQ':
    case 'SGRQ':
    case 'LCADL':
    case 'PFSDQ-M':
      return payload;
    case 'CAT':
    case 'ACT':
    case 'HADS':
    case 'MLHFQ':
      return Array.isArray(payload.answers) ? payload.answers : undefined;
    case 'mMRC':
      return typeof payload.grade === 'number' ? payload.grade : undefined;
    default:
      return undefined;
  }
};

const getPreviewStatusCopy = (preview: AssessmentInviteV2Preview | null) => {
  if (!preview) return null;
  switch (preview.status) {
    case 'completed':
      return 'Este link já foi utilizado e concluído.';
    case 'revoked':
      return 'Este link foi revogado pela clínica.';
    case 'expired':
      return 'Este link expirou e não pode mais ser usado.';
    default:
      return null;
  }
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const message = typeof (error as { message?: unknown }).message === 'string' ? (error as { message: string }).message : '';
    if (message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
};

export default function PublicAssessmentInvitePage() {
  const { tenantSlug, token } = useParams<{ tenantSlug: string; token: string }>();
  const [preview, setPreview] = useState<AssessmentInviteV2Preview | null>(null);
  const [startedInvite, setStartedInvite] = useState<AssessmentInviteV2StartPayload | null>(null);
  const [savedResults, setSavedResults] = useState<Partial<Record<AssessmentV2TestKey, AssessmentResultV2>>>({});
  const [activeTest, setActiveTest] = useState<AssessmentV2TestKey | null>(null);
  const [birthDate, setBirthDate] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [startingInvite, setStartingInvite] = useState(false);
  const [submittingStep, setSubmittingStep] = useState(false);
  const [complete, setComplete] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantSlug || !token) {
      setPageError('Link inválido.');
      setLoadingPreview(false);
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      try {
        setLoadingPreview(true);
        setPageError(null);
        const nextPreview = await AssessmentInvitesV2Service.getPublicPreview(tenantSlug, token);
        if (cancelled) return;
        setPreview(nextPreview);
      } catch (error: any) {
        if (cancelled) return;
        console.error('Erro ao carregar preview público da avaliação:', error);
        setPageError('Não foi possível validar este link de avaliação.');
      } finally {
        if (!cancelled) {
          setLoadingPreview(false);
        }
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
    };
  }, [tenantSlug, token]);

  const selectedTests = useMemo(
    () => normalizeQuestionnaireTests(startedInvite?.selectedTests ?? preview?.selectedTests ?? []),
    [preview?.selectedTests, startedInvite?.selectedTests],
  );

  const unsupportedTests = useMemo(
    () =>
      (startedInvite?.selectedTests ?? preview?.selectedTests ?? []).filter(
        (testKey) => !isAssessmentV2QuestionnaireTest(testKey),
      ),
    [preview?.selectedTests, startedInvite?.selectedTests],
  );

  const progressCount = useMemo(
    () => selectedTests.filter((testKey) => savedResults[testKey] && savedResults[testKey]?.status !== 'cancelled').length,
    [savedResults, selectedTests],
  );

  const handleStartInvite = async () => {
    if (!tenantSlug || !token) {
      toast.error('Link inválido.');
      return;
    }

    if (!birthDate) {
      toast.error('Informe a data de nascimento para validar o acesso.');
      return;
    }

    try {
      setStartingInvite(true);
      const payload = await AssessmentInvitesV2Service.startPublicInvite(tenantSlug, token, birthDate);
      const nextSavedResults = mapResultsByTest(payload.existingResults);

      setStartedInvite(payload);
      setSavedResults(nextSavedResults);
      setActiveTest(getNextPendingQuestionnaire(normalizeQuestionnaireTests(payload.selectedTests), nextSavedResults, null));
      setComplete(payload.status === 'completed');
    } catch (error: any) {
      console.error('Erro ao iniciar link público da avaliação:', error);
      toast.error(getErrorMessage(error, 'Não foi possível validar a data de nascimento para este link.'));
    } finally {
      setStartingInvite(false);
    }
  };

  const handleSubmitQuestionnaire = async (testKey: AssessmentV2TestKey, rawResult: unknown) => {
    if (!tenantSlug || !token || !startedInvite) {
      toast.error('Sessão pública da avaliação indisponível.');
      return;
    }

    try {
      setSubmittingStep(true);
      const assessmentDateIso =
        rawResult && typeof rawResult === 'object' && typeof (rawResult as Record<string, unknown>).finishedAt === 'string'
          ? ((rawResult as Record<string, unknown>).finishedAt as string)
          : new Date().toISOString();

      const sharedContext = buildQuestionnaireSharedContext({
        assessmentDateIso,
        patientContext: {
          birthDate: startedInvite.patientContext.birthDate,
          sex: startedInvite.patientContext.sex,
          weightKg: startedInvite.patientContext.weightKg,
          heightCm: startedInvite.patientContext.heightCm,
          identifier: startedInvite.patientContext.identifier,
        },
        professionalContext: startedInvite.professionalContext,
      });

      const groupIndex = selectedTests.indexOf(testKey) + 1;
      const resultInput = buildQuestionnaireAssessmentResultInput({
        sessionId: startedInvite.sessionId,
        clientId: startedInvite.clientId,
        patientId: startedInvite.patientId,
        testKey,
        rawResult,
        sharedContext,
        groupIndex: groupIndex > 0 ? groupIndex : 1,
        groupTotal: selectedTests.length || 1,
        status: 'completed',
        notes: startedInvite.notes || null,
        patientNameSnapshot: startedInvite.patientName,
        patientMedicalRecordSnapshot: startedInvite.patientContext.identifier,
        professionalUserId: startedInvite.professionalContext.userId ?? startedInvite.createdByUserId ?? null,
        professionalNameSnapshot: startedInvite.professionalContext.name || 'Profissional',
        professionalRegistrationSnapshot: startedInvite.professionalContext.registration,
        professionalRoleSnapshot: startedInvite.professionalContext.role,
        createdByUserId: startedInvite.createdByUserId ?? null,
      });

      const saved = await AssessmentInvitesV2Service.submitPublicStep({
        tenantSlug,
        token,
        birthDate,
        testKey,
        assessmentDate: resultInput.assessmentDate,
        notes: resultInput.notes,
        summaryJson: resultInput.summaryJson,
        payloadJson: resultInput.payloadJson,
        assetsJson: resultInput.assetsJson,
      });

      const nextResult: AssessmentResultV2 = {
        resultId: saved.resultId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ...resultInput,
      };

      setSavedResults((current) => {
        const nextResults = {
          ...current,
          [testKey]: nextResult,
        };

        const nextPending = getNextPendingQuestionnaire(selectedTests, nextResults, testKey);
        setActiveTest(nextPending);

        if (!nextPending) {
          void AssessmentInvitesV2Service.completePublicInvite(tenantSlug, token, birthDate)
            .then(() => {
              setComplete(true);
              setStartedInvite((currentInvite) =>
                currentInvite
                  ? {
                      ...currentInvite,
                      status: 'completed',
                      completedAt: new Date().toISOString(),
                    }
                  : currentInvite,
              );
            })
            .catch((error) => {
              console.error('Erro ao concluir invite público da avaliação:', error);
              toast.error('As respostas foram salvas, mas a conclusão final do link falhou.');
            });
        }

        return nextResults;
      });
    } catch (error: any) {
      console.error('Erro ao salvar questionário público:', error);
      toast.error(getErrorMessage(error, `Não foi possível salvar ${getAssessmentV2Label(testKey)}.`));
    } finally {
      setSubmittingStep(false);
    }
  };

  const renderActiveTest = () => {
    if (!activeTest) {
      return (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-white/70 p-10 text-center text-sm text-slate-500">
          Todos os questionários selecionados foram respondidos.
        </div>
      );
    }

    const commonProps = {
      onCancel: () => setActiveTest(null),
    };

    switch (activeTest) {
      case 'CAT':
        return <CATTest {...commonProps} initial={getInitialQuestionnaireValue('CAT', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('CAT', result)} />;
      case 'ACT':
        return <ACTTest {...commonProps} initial={getInitialQuestionnaireValue('ACT', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('ACT', result)} />;
      case 'mMRC':
        return <MMRCTest {...commonProps} initial={getInitialQuestionnaireValue('mMRC', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('mMRC', result)} />;
      case 'PFSDQ-M':
        return <PFSDQMTest {...commonProps} initial={getInitialQuestionnaireValue('PFSDQ-M', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('PFSDQ-M', result)} />;
      case 'HADS':
        return <HADSTest {...commonProps} initial={getInitialQuestionnaireValue('HADS', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('HADS', result)} />;
      case 'PSQI':
        return <PSQITest {...commonProps} initial={getInitialQuestionnaireValue('PSQI', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('PSQI', result)} />;
      case 'AQLQ':
        return <AQLQTest {...commonProps} initial={getInitialQuestionnaireValue('AQLQ', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('AQLQ', result)} />;
      case 'SGRQ':
        return <SGRQTest {...commonProps} initial={getInitialQuestionnaireValue('SGRQ', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('SGRQ', result)} />;
      case 'LCADL':
        return <LCADLTest {...commonProps} initial={getInitialQuestionnaireValue('LCADL', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('LCADL', result)} />;
      case 'MLHFQ':
        return <MLHFQTest {...commonProps} initial={getInitialQuestionnaireValue('MLHFQ', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('MLHFQ', result)} />;
      case 'SAQ':
        return <SAQTest {...commonProps} initial={getInitialQuestionnaireValue('SAQ', savedResults)} onNext={(result) => void handleSubmitQuestionnaire('SAQ', result)} />;
      default:
        return null;
    }
  };

  if (loadingPreview) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-6 py-10 text-white">
        <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-5 py-3">
          <Loader2 className="h-5 w-5 animate-spin" />
          Validando link da avaliação...
        </div>
      </div>
    );
  }

  const statusCopy = getPreviewStatusCopy(preview);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#e0f2fe,_#f8fafc_40%,_#e2e8f0_100%)] px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="overflow-hidden rounded-[32px] border border-slate-200 bg-white/90 shadow-2xl shadow-slate-300/40 backdrop-blur">
          <div className="border-b border-slate-200 bg-slate-950 px-6 py-6 text-white">
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-sky-500/20 p-3">
                <Link2 className="h-6 w-6 text-sky-300" />
              </div>
              <div>
                <div className="text-xs font-black uppercase tracking-[0.22em] text-sky-300">Avaliação por link</div>
                <h1 className="mt-2 text-2xl font-black tracking-tight">
                  {preview?.clientName || startedInvite?.clientName || 'Clínica'}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-300">
                  Responda os questionários enviados pela clínica. O acesso é restrito ao tenant e validado com data de nascimento.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 px-6 py-6 lg:grid-cols-[320px_minmax(0,1fr)]">
            <aside className="space-y-4">
              <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                  <ShieldCheck className="h-4 w-4" />
                  Segurança
                </div>
                <div className="mt-3 text-sm text-slate-700">
                  Paciente: <span className="font-semibold text-slate-950">{preview?.patientLabel || startedInvite?.patientLabel || 'Paciente'}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                  <Clock3 className="h-4 w-4" />
                  Expira em {preview ? new Date(preview.expiresAt).toLocaleString('pt-BR') : '—'}
                </div>
                <div className="mt-2 text-sm text-slate-600">
                  Progresso: <span className="font-semibold text-slate-950">{progressCount || preview?.progressCount || 0}/{selectedTests.length || preview?.progressTotal || 0}</span>
                </div>
              </div>

              {!!unsupportedTests.length && (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  Este link inclui avaliações que precisam ser concluídas diretamente com a equipe da clínica: {unsupportedTests.map(getAssessmentV2Label).join(', ')}.
                </div>
              )}

              {!startedInvite && !statusCopy && (
                <div className="rounded-3xl border border-slate-200 bg-white p-4">
                  <label htmlFor="invite-birth-date" className="block text-sm font-semibold text-slate-800">
                    Confirme sua data de nascimento
                  </label>
                  <input
                    id="invite-birth-date"
                    type="date"
                    value={birthDate}
                    onChange={(event) => setBirthDate(event.target.value)}
                    className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  />
                  <button
                    type="button"
                    onClick={() => void handleStartInvite()}
                    disabled={startingInvite || !preview || !!unsupportedTests.length}
                    className="mt-4 inline-flex w-full items-center justify-center rounded-xl bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {startingInvite ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                    Validar e começar
                  </button>
                </div>
              )}

              <div className="rounded-3xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Questionários</div>
                <div className="mt-3 space-y-2">
                  {selectedTests.length ? (
                    selectedTests.map((testKey) => {
                      const completed = Boolean(savedResults[testKey] && savedResults[testKey]?.status !== 'cancelled');
                      const active = activeTest === testKey;
                      return (
                        <button
                          key={testKey}
                          type="button"
                          onClick={() => startedInvite && setActiveTest(testKey)}
                          disabled={!startedInvite}
                          className={`flex w-full items-center justify-between rounded-2xl border px-3 py-2.5 text-left text-sm transition ${
                            active
                              ? 'border-sky-300 bg-sky-50 text-sky-950'
                              : completed
                                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                                : 'border-slate-200 bg-white text-slate-700'
                          } ${startedInvite ? 'hover:border-sky-200' : 'cursor-default opacity-70'}`}
                        >
                          <span className="font-medium">{getAssessmentV2Label(testKey)}</span>
                          {completed ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs uppercase tracking-[0.18em]">Pendente</span>}
                        </button>
                      );
                    })
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
                      Nenhum questionário disponível neste link.
                    </div>
                  )}
                </div>
              </div>
            </aside>

            <main className="space-y-4">
              {pageError ? (
                <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-900">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <AlertTriangle className="h-5 w-5" />
                    Link inválido
                  </div>
                  <p className="mt-3 text-sm">{pageError}</p>
                </div>
              ) : statusCopy && !startedInvite ? (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-950">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <AlertTriangle className="h-5 w-5" />
                    Link indisponível
                  </div>
                  <p className="mt-3 text-sm">{statusCopy}</p>
                </div>
              ) : complete ? (
                <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 text-emerald-950">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-8 w-8" />
                    <div>
                      <div className="text-sm font-black uppercase tracking-[0.18em] text-emerald-700">Envio concluído</div>
                      <h2 className="mt-1 text-2xl font-black tracking-tight">Questionários recebidos com sucesso</h2>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed">
                    As respostas foram registradas no ambiente da clínica e ficarão disponíveis para revisão profissional.
                  </p>
                </div>
              ) : !startedInvite ? (
                <div className="rounded-3xl border border-slate-200 bg-white p-8 text-slate-700">
                  <h2 className="text-xl font-black tracking-tight text-slate-950">Pronto para responder</h2>
                  <p className="mt-3 text-sm leading-relaxed">
                    Após confirmar sua data de nascimento, você poderá responder os questionários enviados pela clínica no tenant correto.
                  </p>
                </div>
              ) : (
                <>
                  <div className="rounded-3xl border border-slate-200 bg-white px-5 py-4">
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Paciente validado</div>
                    <div className="mt-2 text-lg font-semibold text-slate-950">{startedInvite.patientName}</div>
                    {startedInvite.notes ? <div className="mt-2 text-sm text-slate-600">{startedInvite.notes}</div> : null}
                  </div>

                  {submittingStep && (
                    <div className="rounded-3xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                      Salvando questionário atual...
                    </div>
                  )}

                  {renderActiveTest()}
                </>
              )}
            </main>
          </div>
        </section>
      </div>
    </div>
  );
}
